import React, { useState, useEffect } from 'react'
import type { PresentationIndustry, QuoteDocument, QuoteFile, PresentationLibrary } from 'paintscout'
import { getFeature, shouldAddToLibrary } from '@paintscout/util/builder'
import {
  PopupMenuItem,
  makeStyles,
  ClickAwayListener,
  PresentationFilePreview as FilePreview,
  ImageSmallIcon
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses, PresentationFilePreviewProps as FilePreviewProps } from '@paintscout/ui'
import { Placeholder, PresEditorTooltip, ViewMediaDialog } from '../../components'
import YouTubeIcon from '@material-ui/icons/YouTube'
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined'
import SyncIcon from '@material-ui/icons/Sync'
import { PresentationLibraryDialog } from '../../dialogs'
import isEqual from 'lodash/isEqual'
import classnames from 'classnames'
import { useClientOptions, useDialogs, PresentationEmbedMedia as EmbedMedia } from '@paintscout/ui-business'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { useQuote, usePresentation } from '../../../context'
import { clearDrillInOnClickAway } from '../../util/presentationEditorDom'

export interface SmartSourceOption {
  key: string
  label: string
  getFile: (quote: QuoteDocument) => QuoteFile | null
}

interface InlineEditableMediaProps {
  accept?: string
  presentationLibrary?: {
    addFilesToLibrary: (args: { files: QuoteFile[] }) => void
    library: PresentationLibrary
    saveLibrary: (args: { library: PresentationLibrary }) => void
  }
  classes?: StyleClasses<typeof useStyles>
  file: QuoteFile
  getFocusFromSection?: boolean
  handleEdit: (content: QuoteFile) => void
  hasRightSideMenus?: boolean
  isAnimated?: boolean
  isEditable?: boolean
  isSettings?: boolean
  mediaOptionsProps?: {
    blockPrefix: string
    fieldSuffix: string
    hideAspectOptions?: boolean
  }
  placeholderButtonText?: string
  presentationIndustry: PresentationIndustry
  showAllPages?: boolean
  square?: boolean
  youtube?: boolean
  filePreviewProps?: Partial<FilePreviewProps>
  /** Available "smart content" sources (e.g. "first estimate image") this field can bind to. */
  smartSourceOptions?: SmartSourceOption[]
  /** Currently-selected smart source key (null/undefined = manual). */
  smartSourceKey?: string | null
  /** Persists the smart source selection (a sibling field, alongside the manual file value). */
  onSmartSourceChange?: (key: string | null) => void
}

const useStyles = makeStyles<Theme, InlineEditableMediaProps>(
  (theme: Theme) => {
    return {
      root: (props) => ({
        display: 'flex',
        justifyContent: props.file?.presentationOptions?.horizontalAlign || 'center',
        minHeight: 50
      }),
      size: {
        position: 'relative',
        width: '100%',
        height: '100%'
      },
      hovered: {
        cursor: 'pointer',
        '&:hover': {
          outline: `2px solid ${theme.palette.presentation.sectionBorder.hover}`,
          zIndex: 1,
          '& $iconMenuRoot': {
            display: 'flex'
          }
        }
      },
      focused: {
        outline: `2px solid ${theme.palette.presentation.sectionBorder.focus}`,
        zIndex: 1
      },
      image: (
        props,
        clipPath = props.file?.presentationOptions?.clipPath || 'none',
        clipPathOrientation = props.file?.presentationOptions?.clipPathOrientation || 'top'
      ) => ({
        '&$image': {
          width: '100%',
          aspectRatio: getAspectRatio({ file: props.file, maxSquare: false }),
          objectPosition: getObjectPosition(props.file),
          clipPath: getClipPath(clipPath, clipPathOrientation),
          objectFit: props.file?.presentationOptions?.aspectRatio !== 'unset' ? 'cover' : 'contain',
          [theme.breakpoints.down('sm')]: {
            aspectRatio: getAspectRatio({ file: props.file, maxSquare: true })
          },
          borderRadius: 0
        }
      }),
      youtubeWrapper: {
        pointerEvents: 'auto'
      },
      dropzone: (props) => ({
        height: props.file ? 'auto' : 150
      }),
      editable: {
        cursor: 'pointer',
        height: 'inherit'
      },
      mediaPlaceholder: {
        aspectRatio: '1/1'
      },
      iconMenuRoot: {
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.borderRadius.md,
        bottom: theme.spacing(2),
        boxShadow: theme.boxShadow[1],
        display: 'none',
        left: theme.spacing(2),
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        zIndex: 3
      },
      iconMenuRightSide: {
        right: theme.spacing(2),
        left: 'auto'
      },
      iconMenuRootFocused: {
        display: 'flex'
      }
    }
  },
  { name: 'InlineEditableMedia' }
)

export default function InlineEditableMedia(props: InlineEditableMediaProps) {
  const classes = useStyles(props)
  const {
    accept,
    presentationLibrary,
    getFocusFromSection,
    handleEdit,
    hasRightSideMenus,
    isAnimated,
    isEditable,
    isSettings,
    mediaOptionsProps,
    placeholderButtonText,
    presentationIndustry,
    showAllPages,
    square,
    youtube: hasYoutube = true,
    filePreviewProps,
    smartSourceOptions,
    smartSourceKey,
    onSmartSourceChange
  } = props

  const { blockPrefix, fieldSuffix } = mediaOptionsProps || {}
  const { options, clientId } = useClientOptions()
  const s3UploadFeatureEnabled = getFeature({ options, path: 's3ImageUpload.enabled' })
  const { s3MediaUpload: s3MediaUploadEnabled } = useFlags()
  const { quote } = useQuote()

  const [file, setFile] = useState(props.file)
  // isFocused mirrors the parent block's own focus (isEditable) — every field in a focused block is
  // immediately interactive in one click; a *second* click on this specific image is what drills into
  // its own options (see the onClick below). Drives the outline/hover-menu CSS, gates the YouTube URL
  // input (EmbedMedia's `disabled` prop), and the Logo/Banner-poster getFocusFromSection auto-open for
  // an *empty* slot.
  const isFocused = isEditable
  const { focusedElement, setFocusedElement } = usePresentation()

  // this field's slot in the inspector's drill-in state (#22201) — the formik path is unique per
  // rendered field, since blockPrefix carries the page/section/block indices
  const optionsKey = `media::${blockPrefix}.${fieldSuffix}`
  const isDrilledIn = focusedElement === optionsKey
  const format = file?.format
  const isYoutube = format === 'youtube'
  const isPdf = format === 'pdf'
  const { openDialog, dismissDialog, stack: dialogStack } = useDialogs()
  const { addFilesToLibrary, library } = presentationLibrary || {}

  // A smart source binds this field to a live, quote-derived image (e.g. "first estimate
  // image") instead of a fixed one — resolved fresh from whichever quote is in context (the
  // editor's preview quote, or the actual customer's quote in customer-view), so the same
  // template shows the right image per quote. The manually-picked `file` is preserved
  // independently underneath so switching the smart source off reverts to it.
  const activeSmartSource = smartSourceOptions?.find((option) => option.key === smartSourceKey)
  const resolvedFile = activeSmartSource ? activeSmartSource.getFile(quote) ?? file : file

  const handleToggleSmartSource = (key: string) => {
    onSmartSourceChange?.(smartSourceKey === key ? null : key)
  }

  const handlePdfPreview = (_page) => {
    openDialog(ViewMediaDialog, {
      files: [resolvedFile],
      enableDownload: !isSettings,
      onClose: dismissDialog
    })
  }

  const handleMediaLibraryDialog = () => {
    openDialog(PresentationLibraryDialog, {
      handleSelect: handleLibrarySelect,
      presentationLibrary,
      presentationIndustry,
      specificQuery: file?.pexelsQuery,
      onCancel: () => dismissDialog()
    })
  }

  // update file in case of prop.file change (e.g., image to image swap from ThreeColumnOptions)
  // note: this is also triggered by handleEdit() in this component
  useEffect(() => {
    setFile(props.file)
  }, [props.file])

  useEffect(() => {
    if (!isEditable) {
      // remove focus if parent block loses focus
      // instead of listening for a blur on the editor, we listen for a click outside of the block
      if (!isEqual(props.file, file)) {
        handleEdit(file)
      }
      if (file.format === 'youtube' && !file.youtubeId) {
        handleChangeFormat('upload')
      }
      if (isDrilledIn) {
        setFocusedElement('')
      }
    }
  }, [isEditable])

  const applyFileChange = (newFile: QuoteFile) => {
    const comboFile = file
      ? {
          ...newFile,
          presentationOptions: file.presentationOptions,
          youtubeId: newFile.youtubeId ?? file.youtubeId
        }
      : newFile
    setFile(comboFile)
    handleEdit(comboFile)
    return comboFile
  }

  // Direct changes (drag/drop upload, youtube URL entry) never touch the library
  // themselves, so auto-add it here if it's new.
  const handleChange = (newFile: QuoteFile) => {
    const comboFile = applyFileChange(newFile)

    if (addFilesToLibrary && shouldAddToLibrary({ file: comboFile, library })) {
      addFilesToLibrary({ files: [comboFile] })
    }
  }

  // PresentationLibraryDialog already adds new uploads/Pexels picks to the library and
  // saves them itself (so it can show a single loading state and close on success) —
  // applying the file change here without also auto-adding avoids a second, racing save
  // to the same library.
  const handleLibrarySelect = (newFile: QuoteFile) => {
    applyFileChange(newFile)
  }

  const handleChangeFormat = (format: QuoteFile['format']) => {
    setFile({ ...file, format })
    handleEdit({ ...file, format }) // this is necessary for video banner blocks to update their block options (not all layouts are available for video) without having to blur the block
  }

  if (!isSettings)
    return (
      <>
        {resolvedFile && (
          <FilePreview
            classes={{
              image: classes.image,
              youtubeWrapper: classes.youtubeWrapper,
              size: classes.size
            }}
            onPageSelect={isPdf && !showAllPages ? handlePdfPreview : null}
            showAllPages={showAllPages}
            square={square}
            className={classes.root}
            file={resolvedFile as any}
            isAnimated={isAnimated}
            inPresentation
            inGallery
            {...filePreviewProps}
          />
        )}
        {!resolvedFile && !isSettings && <></>}
      </>
    )
  else
    return (
      <ClickAwayListener
        onClickAway={(ev) => {
          // Only the drill-in needs clearing here — deselecting the block itself on click-away is the
          // parent block's own ClickAwayListener's job (Presentation.tsx), which already runs for any
          // click outside the block, isFocused/isEditable included. `skip: getFocusFromSection` avoids
          // dropping focus from an element that just auto-selected itself.
          clearDrillInOnClickAway({
            target: ev.target,
            isDrilledIn,
            setFocusedElement,
            skip: getFocusFromSection
          })
        }}
        {...(dialogStack.length && { mouseEvent: false, touchEvent: false })}
      >
        <div
          className={classnames({
            [classes.size]: true,
            [classes.hovered]: !isFocused && !!(resolvedFile || hasYoutube || smartSourceOptions?.length),
            [classes.focused]: isFocused
          })}
          onClick={() => {
            if (!resolvedFile) {
              handleMediaLibraryDialog()
            }
          }}
        >
          {(resolvedFile || hasYoutube || smartSourceOptions?.length > 0) && (
            <div
              className={classnames(classes.iconMenuRoot, {
                [classes.iconMenuRootFocused]: isFocused,
                [classes.iconMenuRightSide]: hasRightSideMenus
              })}
            >
              {hasYoutube && (
                <>
                  <PresEditorTooltip title={'Toggle YouTube'}>
                    <PopupMenuItem
                      icon={isYoutube ? ImageOutlinedIcon : YouTubeIcon}
                      iconOnly
                      selected={isYoutube}
                      onClick={(ev) => {
                        ev.stopPropagation()
                        handleChangeFormat(isYoutube ? 'upload' : 'youtube')
                      }}
                    />
                  </PresEditorTooltip>
                </>
              )}
              {smartSourceOptions?.map((option) => (
                <PresEditorTooltip key={option.key} title={option.label}>
                  <PopupMenuItem
                    icon={SyncIcon}
                    iconOnly
                    selected={smartSourceKey === option.key}
                    onClick={(ev) => {
                      ev.stopPropagation()
                      handleToggleSmartSource(option.key)
                    }}
                  />
                </PresEditorTooltip>
              ))}
            </div>
          )}
          {/* Once a file exists (and it isn't youtube), replacing it always goes through the
              Library dialog, so there's no need for the interactive EmbedMedia/Dropzone here —
              a plain FilePreview displays it. */}
          {resolvedFile && !isYoutube && (
            <div
              className={classes.root}
              onClick={(ev) => {
                // Block not focused yet: let the click bubble so it focuses the parent block first,
                // same as clicking any other part of the block.
                if (!isEditable) {
                  return
                }

                // Block already focused: a single click on this image drills straight into its own
                // options; a click after *that* (already drilled in) swaps it via the Library dialog.
                ev.stopPropagation()

                if (!isDrilledIn) {
                  setFocusedElement(optionsKey)
                  return
                }

                handleMediaLibraryDialog()
              }}
            >
              <FilePreview
                classes={{
                  image: classes.image,
                  youtubeWrapper: classes.youtubeWrapper,
                  size: classes.size
                }}
                showAllPages={showAllPages}
                square={square}
                file={resolvedFile as any}
                isAnimated={isAnimated}
                inPresentation
                inGallery
                // only once drilled in, since that's when a click swaps rather than selecting — it's
                // the hint for the next click of that flow
                showSwapOnHover={isDrilledIn}
                {...filePreviewProps}
              />
            </div>
          )}
          {((!resolvedFile && isFocused) || isYoutube) && (
            <div className={classes.root}>
              <EmbedMedia
                classes={classes}
                value={file ?? { visibility: 'visible', format: format }}
                noClearButton
                showSwapOnHover
                square={square}
                accept={accept ?? 'video/*,image/*,application/pdf'}
                maxWidth={1280}
                maxHeight={1600}
                disabled={!isFocused}
                onChange={handleChange}
                youtube={false}
                format={format}
                showAllPages={showAllPages}
                filePreviewProps={filePreviewProps}
                companyId={clientId}
                s3UploadFeatureEnabled={s3UploadFeatureEnabled}
                s3MediaUploadEnabled={s3MediaUploadEnabled}
              />
            </div>
          )}
          {!resolvedFile && !isFocused && (
            <Placeholder
              classes={{ placeholder: classnames(classes.image, classes.mediaPlaceholder) }}
              buttonText={placeholderButtonText}
              Icon={ImageSmallIcon}
            />
          )}
        </div>
      </ClickAwayListener>
    )
}

function getAspectRatio(args: { file: QuoteFile; maxSquare: boolean }) {
  const { file, maxSquare } = args
  if (!file) return 'unset'
  const aspectRatio = (file.presentationOptions?.aspectRatio || 'unset').toString()
  const defaultImageOrientation = file.width < file.height ? 'portrait' : 'landscape'
  const imageOrientation = file.presentationOptions?.imageOrientation || defaultImageOrientation
  if (imageOrientation === 'landscape') return aspectRatio
  else {
    if (maxSquare) return '1 / 1'
    const slashIndex = aspectRatio.indexOf('/')
    if (slashIndex === -1) return aspectRatio
    else return aspectRatio.substring(slashIndex + 1) + '/' + aspectRatio.substring(0, slashIndex)
  }
}

function getObjectPosition(file: QuoteFile) {
  if (!file) return '50% 50%'
  if (file.format === 'pdf') return '50% 0%'
  const objectPositionX = file.presentationOptions?.objectPositionX ?? 50
  const objectPositionY = file.presentationOptions?.objectPositionY ?? 50
  return `${objectPositionX}% ${objectPositionY}%`
}

function getClipPath(clipPath: string, clipPathOrientation: 'top' | 'bottom' | 'left' | 'right') {
  // switch
  switch (clipPath) {
    case 'circle':
      return 'circle()'
    case 'parallelogram':
      if (clipPathOrientation === 'top') return 'polygon(0 25%, 100% 0, 100% 75%, 0 100%)'
      else if (clipPathOrientation === 'right') return 'polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%)'
      else if (clipPathOrientation === 'bottom') return 'polygon(0 0, 100% 25%, 100% 100%, 0 75%)'
      else if (clipPathOrientation === 'left') return 'polygon(0% 0%, 75% 0%, 100% 100%, 25% 100%)'
    // falls through
    case 'chevron':
      if (clipPathOrientation === 'top') return 'polygon(100% 20%, 100% 100%, 50% 80%, 0 100%, 0 20%, 50% 0)'
      else if (clipPathOrientation === 'right') return 'polygon(80% 0%, 100% 50%, 80% 100%, 0% 100%, 20% 50%, 0% 0%)'
      else if (clipPathOrientation === 'bottom') return 'polygon(100% 0, 100% 80%, 50% 100%, 0 80%, 0 0, 50% 20%)'
      else if (clipPathOrientation === 'left') return 'polygon(100% 0%, 80% 50%, 100% 100%, 20% 100%, 0% 50%, 20% 0%)'
    // falls through
    case 'scoop':
      if (clipPathOrientation === 'top') return 'ellipse(190% 100% at 100% 100%)'
      else if (clipPathOrientation === 'right') return 'ellipse(100% 190% at 0 100%)'
      else if (clipPathOrientation === 'bottom') return 'ellipse(190% 100% at 0 0)'
      else if (clipPathOrientation === 'left') return 'ellipse(100% 190% at 100% 0)'
    // falls through
    case 'arch':
      if (clipPathOrientation === 'top') return 'inset(0 round 9999px 9999px 0% 0%)'
      else if (clipPathOrientation === 'right') return 'inset(0 round 0 9999px 9999px 0)'
      else if (clipPathOrientation === 'bottom') return 'inset(0 round 0 0 9999px 9999px)'
      else if (clipPathOrientation === 'left') return 'inset(0 round 9999px 0 0 9999px)'
    // falls through
    case 'leaf':
      if (clipPathOrientation === 'right' || clipPathOrientation === 'top') return 'inset(0 round 9999px 0 9999px 0)'
      else if (clipPathOrientation === 'left' || clipPathOrientation === 'bottom')
        return 'inset(0 round 0 9999px 0 9999px)'
    // falls through
    case 'drop':
      if (clipPathOrientation === 'top') return 'inset(0 round 0 9999px 9999px 9999px)'
      else if (clipPathOrientation === 'right') return 'inset(0 round 9999px 0 9999px 9999px)'
      else if (clipPathOrientation === 'bottom') return 'inset(0 round 9999px 9999px 0 9999px)'
      else if (clipPathOrientation === 'left') return 'inset(0 round 9999px 9999px 9999px 0)'
    // falls through
    case 'none':
      return 'none'
  }
}
